import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getDoc,
  doc,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { getAuth } from "firebase/auth";
import { saveResponse } from "../db";
import InfoModal from "./InfoModal";
import SurveyQuestions from "./SurveyQuestions";
import { getTenantIdFromHost } from "../Tenant/getTenantId";

const SurveyForm = () => {
  const auth = getAuth();
  const tenantId = getTenantIdFromHost();
  const { surveyId } = useParams();
  const navigate = useNavigate();

  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [responses, setResponses] = useState([]);
  const [comments, setComments] = useState({});
  const [errors, setErrors] = useState({});
  const [employeeId, setEmployeeId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [officeId, setOfficeId] = useState("");
  const [employeeIds, setEmployeeIds] = useState([]);
  const [officeIds, setOfficeIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const [modalConfig, setModalConfig] = useState({
    open: false,
    title: "",
    text: "",
    btn1: "Ok",
    btn2: null,
    onCancel: null,
    onConfirm: null,
  });

  // const isDev = window.location.hostname === "localhost";
  const isDev = false;
  const DEFAULT_MODE = "visible";
  const [formMode, setFormMode] = useState(DEFAULT_MODE);

  const openModal = ({
    title = "",
    text = "",
    btn1 = "Ok",
    btn2 = null,
    onCancel = null,
    onConfirm = null,
  }) => {
    setModalConfig({
      open: true,
      title,
      text,
      btn1,
      btn2,
      onCancel,
      onConfirm,
    });
  };

  const closeModal = () => {
    setModalConfig((prev) => ({
      ...prev,
      open: false,
      onCancel: null,
      onConfirm: null,
    }));
  };

  const matchesMode = (formData) => {
    const hidden = formData?.hidden === true;
    if (!isDev) return hidden === false;
    if (formMode === "hidden") return hidden;
    if (formMode === "all") return true;
    return !hidden;
  };

  const isQuestionAnswered = (question, response) => {
    if (!question) return true;

    // textfrågor frivilliga
    //if (question.type === "text") return true;

    if (Array.isArray(response)) return response.length > 0;

    return response !== null && response !== undefined && response !== "";
  };

  const isFormValid = useMemo(() => {
    if (!selectedForm) return false;
    if (!employeeId.trim()) return false;
    if (!officeId.trim()) return false;
    if (!customerId.trim()) return false;

    return selectedForm.questions.every((question, index) =>
      isQuestionAnswered(question, responses[index]),
    );
  }, [selectedForm, employeeId, officeId, customerId, responses]);

  useEffect(() => {
    if (!surveyId) return;

    const loadFormData = async () => {
      const docRef = doc(db, "tenants", tenantId, "forms", surveyId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const formData = docSnap.data();
        setSelectedForm({ ...formData, id: surveyId });
        setResponses(new Array(formData.questions.length).fill(""));
        setComments({});
      }
    };

    loadFormData();
  }, [surveyId, tenantId]);

  const getResponsesCount = async (surveyId) => {
    if (!surveyId) return 0;

    const q = query(
      collection(db, "tenants", tenantId, "responses"),
      where("surveyId", "==", surveyId),
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  };

  const loadResponsesForVisibleForms = async () => {
    const formsSnapshot = await getDocs(
      query(collection(db, "tenants", tenantId, "forms")),
    );

    const surveyIds = formsSnapshot.docs
      .filter((d) => matchesMode(d.data()))
      .map((d) => d.id);

    if (surveyIds.length === 0) {
      setEmployeeIds([]);
      setOfficeIds([]);
      return;
    }

    const allResponses = [];

    for (const id of surveyIds) {
      const responseQuery = query(
        collection(db, "tenants", tenantId, "responses"),
        where("surveyId", "==", id),
      );
      const responseSnapshot = await getDocs(responseQuery);
      responseSnapshot.forEach((doc) => allResponses.push(doc.data()));
    }

    setEmployeeIds(
      Array.from(
        new Set(allResponses.map((r) => r.employeeId).filter(Boolean)),
      ),
    );

    setOfficeIds(
      Array.from(new Set(allResponses.map((r) => r.officeId).filter(Boolean))),
    );
  };

  const loadForms = async () => {
    const q = query(collection(db, "tenants", tenantId, "forms"));
    const querySnapshot = await getDocs(q);

    const docs = querySnapshot.docs.filter((d) => matchesMode(d.data()));

    const formList = [];
    for (const docSnap of docs) {
      const formData = docSnap.data();
      const respCount = await getResponsesCount(docSnap.id);

      formList.push({
        id: docSnap.id,
        respCount,
        ...formData,
      });
    }

    setForms(formList);
  };

  useEffect(() => {
    loadForms();
    loadResponsesForVisibleForms();
  }, [formMode, tenantId]);

  const doCloseForm = () => {
    setSelectedForm(null);
    setResponses([]);
    setComments({});
    setErrors({});
    setEmployeeId("");
    setCustomerId("");
    setOfficeId("");
    navigate(`/survey`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    if (!employeeId.trim()) {
      openModal({
        title: "!",
        text: "Du behöver fylla id för ansvarig handläggare",
        btn1: "Ok!",
        onCancel: closeModal,
      });
      return;
    }

    if (!officeId.trim()) {
      openModal({
        title: "!",
        text: "Du behöver fylla id för kontoret",
        btn1: "Ok!",
        onCancel: closeModal,
      });
      return;
    }

    if (!customerId.trim()) {
      openModal({
        title: "!",
        text: "Du behöver fylla i ett kund-id!",
        btn1: "Ok!",
        onCancel: closeModal,
      });
      return;
    }

    const allAnswered = selectedForm.questions.every((question, index) =>
      isQuestionAnswered(question, responses[index]),
    );

    if (!allAnswered) {
      openModal({
        title: "!",
        text: "Du måste besvara alla obligatoriska frågor innan du kan skicka in.",
        btn1: "Ok!",
        onCancel: closeModal,
      });
      return;
    }

    setIsSaving(true);

    try {
      const q = query(
        collection(db, "tenants", tenantId, "responses"),
        where("surveyId", "==", selectedForm.id),
      );
      const snapshot = await getDocs(q);

      const custId = customerId.trim().toLowerCase();
      const offId = officeId.trim().toLowerCase();
      const empId = employeeId.trim().toLowerCase();

      let finalEmployeeId = employeeId.trim();
      let finalOfficeId = officeId.trim();
      let isCustomerIdTaken = false;

      snapshot.forEach((doc) => {
        const data = doc.data();

        if (data.customerId?.toLowerCase() === custId) {
          isCustomerIdTaken = true;
        }

        if (data.employeeId && data.employeeId.toLowerCase() === empId) {
          finalEmployeeId = data.employeeId;
        }

        if (data.officeId && data.officeId.toLowerCase() === offId) {
          finalOfficeId = data.officeId;
        }
      });

      if (isCustomerIdTaken) {
        openModal({
          title: "!",
          text: "En kund med detta id har redan fyllt i enkäten.",
          btn1: "Ok!",
          onCancel: closeModal,
        });
        return;
      }

      const responseData = {
        surveyId: selectedForm.id,
        employeeId: finalEmployeeId,
        officeId: finalOfficeId,
        customerId: customerId.trim(),
        responses,
        comments,
        timestamp: serverTimestamp(),
        createdBy: auth.currentUser?.email || "unknown",
        status: "submitted",
      };

      await saveResponse(responseData);

      // setForms((prev) =>
      //   prev.map((f) =>
      //     f.id === responseData.surveyId
      //       ? {
      //           ...f,
      //           respCount: (f.respCount ?? 0) + 1,
      //         }
      //       : f,
      //   ),
      // );

      setResponses(new Array(selectedForm.questions.length).fill(""));
      setComments({});
      setErrors({});

      openModal({
        title: "✓",
        text: "Svaret är sparat!",
        btn1: "Ok!",
        onCancel: () => {
          closeModal();
          doCloseForm();
        },
      });
    } catch (err) {
      console.error(err);
      openModal({
        title: "✕",
        text: "Kunde inte spara svaret. Försök igen.",
        btn1: "Ok!",
        onCancel: closeModal,
      });
    } finally {
      setIsSaving(false);
      loadForms();
    }
  };

  const handleStartSurvey = (surveyId) => {
    navigate(`/survey/${surveyId}`);
  };

  const handleEditSurvey = (surveyId) => {
    navigate(`/builder/${surveyId}`);
  };

  const handleEmailSurvey = (surveyId) => {
    navigate(`/createInvite/${surveyId}`);
  };

  const handleArchiveSurvey = async (surveyId) => {
    const confirmArchive = window.confirm(
      "Vill du verkligen arkivera detta formulär?",
    );

    if (confirmArchive) {
      await updateDoc(doc(db, "tenants", tenantId, "forms", surveyId), {
        hidden: true,
      });

      loadForms();

      openModal({
        title: "✓",
        text: "Formuläret har arkiverats!",
        btn1: "Ok!",
        onCancel: closeModal,
      });
    } else {
      openModal({
        title: "!",
        text: "Arkivering avbröts.",
        btn1: "Ok!",
        onCancel: closeModal,
      });
    }
  };

  const handleCloseForm = () => {
    const hasFilledResponses = responses.some((r) => {
      if (Array.isArray(r)) return r.length > 0;
      return r !== null && r !== undefined && r !== "";
    });

    const hasChanges =
      hasFilledResponses ||
      employeeId.trim() ||
      customerId.trim() ||
      officeId.trim();

    if (hasChanges) {
      openModal({
        title: "Vill du stänga formuläret?",
        text: "Eventuella ändringar som inte sparats kommer att gå förlorade.",
        btn1: "Stanna kvar",
        btn2: "Stäng ändå",
        onCancel: closeModal,
        onConfirm: () => {
          closeModal();
          doCloseForm();
        },
      });
    } else {
      doCloseForm();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.target.type !== "submit") {
      e.preventDefault();
    }
  };

  return (
    <>
      <div className="formBuilderWrapper">
        {!selectedForm ? (
          <div>
            <h1>Undersökningar</h1>

            {isDev && (
              <select
                value={formMode}
                onChange={(e) => setFormMode(e.target.value)}
              >
                <option value="hidden">Hidden (testdata)</option>
                <option value="visible">Synliga</option>
                <option value="all">Alla</option>
              </select>
            )}

            <ul className="surveyList">
              {forms.map((form) => (
                <li key={form.id}>
                  <div>
                    <h3>
                      Undersökning: {form.formName} | antal svar (
                      {form.respCount})
                    </h3>

                    <p>
                      Skapad datum: {form.createdAt.toDate().toLocaleString()}
                    </p>

                    <div className="surveyBtnWrapper">
                      <button onClick={() => handleStartSurvey(form.id)}>
                        Genomför undersökning
                      </button>

                      <button onClick={() => handleEmailSurvey(form.id)}>
                        Skapa inbjudan till enkät
                      </button>

                      {(form.respCount ?? 0) > 0 ? null : (
                        <button
                          onClick={() => handleEditSurvey(form.id)}
                          className="editButton"
                        >
                          Redigera undersökning
                        </button>
                      )}

                      {!form.hidden ? (
                        <button
                          className="deleteButton"
                          onClick={() => handleArchiveSurvey(form.id)}
                        >
                          x
                        </button>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <button className="closeButton" onClick={handleCloseForm}></button>
        )}

        {selectedForm ? (
          <>
            <div className="sectionHeader">
              <h2>Genomför undersökning</h2>
            </div>

            <hr />

            <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
              <h2>{selectedForm.formName}</h2>
              <h4>
                Created on: {selectedForm.createdAt.toDate().toLocaleString()}
              </h4>

              <div>
                <label>Ansvarig handläggare ID:</label>
                <input
                  type="text"
                  list="employee-options"
                  placeholder="Ange eller välj id för ansvarig handläggare"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                />

                {employeeIds.length > 0 && (
                  <datalist id="employee-options">
                    {employeeIds.map((id) => (
                      <option key={id} value={id} />
                    ))}
                  </datalist>
                )}

                <label>Kontor ID:</label>
                <input
                  type="text"
                  list="office-options"
                  placeholder="Ange eller välj id för kontor"
                  value={officeId}
                  onChange={(e) => setOfficeId(e.target.value)}
                />

                {officeIds.length > 0 && (
                  <datalist id="office-options">
                    {officeIds.map((id) => (
                      <option key={id} value={id} />
                    ))}
                  </datalist>
                )}

                <label>Kund ID:</label>
                <input
                  type="text"
                  placeholder="Ange ID för kund"
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                />

                <hr />
              </div>

              <SurveyQuestions
                questions={selectedForm.questions}
                responses={responses}
                setResponses={setResponses}
                comments={comments}
                setComments={setComments}
                errors={errors}
                setErrors={setErrors}
              />

              <button disabled={isSaving} type="submit">
                {isSaving ? "Sparar..." : "Skicka in svar"}
              </button>
              {/* <p>{isFormValid ?"VALID" : "NOT VALID"}</p> */}
            </form>
          </>
        ) : null}
      </div>

      {modalConfig.open && (
        <InfoModal
          onCancel={() => {
            if (modalConfig.onCancel) {
              modalConfig.onCancel();
            } else {
              closeModal();
            }
          }}
          onConfirm={() => {
            if (modalConfig.onConfirm) {
              modalConfig.onConfirm();
            } else {
              closeModal();
            }
          }}
          btn1={modalConfig.btn1}
          btn2={modalConfig.btn2}
        >
          <h3>{modalConfig.title}</h3>
          <p>{modalConfig.text}</p>
        </InfoModal>
      )}
    </>
  );
};

export default SurveyForm;
